import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";

const statuses = [
  { value: "active", label: "Aktiv" },
  { value: "inactive", label: "Inaktiv" },
];

export default function TeamForm({ team, onSubmit, onClose, isLoading }) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: "active",
    related_to_type: "none",
    related_to_id: "",
  });

  const { data: projects = [] } = useQuery({
    queryKey: ["projects"],
    queryFn: () => base44.entities.Project.list(),
  });

  const { data: customers = [] } = useQuery({
    queryKey: ["customers"],
    queryFn: () => base44.entities.BusinessCustomer.list(),
  });

  const { data: investors = [] } = useQuery({
    queryKey: ["investors"],
    queryFn: () => base44.entities.Investor.list(),
  });

  useEffect(() => {
    if (team) {
      setFormData({
        name: team.name || "",
        description: team.description || "",
        status: team.status || "active",
        related_to_type: team.related_to_type || "none",
        related_to_id: team.related_to_id || "",
      });
    }
  }, [team]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    let relatedName = null;
    if (formData.related_to_type === "project") {
      const project = projects.find(p => p.id === formData.related_to_id);
      relatedName = project?.name;
    } else if (formData.related_to_type === "customer") {
      const customer = customers.find(c => c.id === formData.related_to_id);
      relatedName = customer?.company_name;
    } else if (formData.related_to_type === "investor") {
      const investor = investors.find(i => i.id === formData.related_to_id);
      relatedName = investor?.name;
    }

    onSubmit({
      ...formData,
      related_to_id: formData.related_to_type !== "none" ? formData.related_to_id : null,
      related_to_name: relatedName,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="text-xl font-semibold text-slate-900">
            {team ? "Rediger Team" : "Opprett Team"}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Teamnavn *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="f.eks. Utviklingsteam"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Beskrivelse</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              placeholder="Hva gjør dette teamet?"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statuses.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="related_to_type">Relatert Til</Label>
              <Select 
                value={formData.related_to_type} 
                onValueChange={(v) => setFormData({ ...formData, related_to_type: v, related_to_id: "" })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Ingen</SelectItem>
                  <SelectItem value="project">Prosjekt</SelectItem>
                  <SelectItem value="customer">Kunde</SelectItem>
                  <SelectItem value="investor">Investor</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.related_to_type === "project" && (
              <div className="space-y-2">
                <Label htmlFor="related_to_id">Velg Prosjekt</Label>
                <Select 
                  value={formData.related_to_id} 
                  onValueChange={(v) => setFormData({ ...formData, related_to_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Velg prosjekt" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {formData.related_to_type === "customer" && (
              <div className="space-y-2">
                <Label htmlFor="related_to_id">Velg Kunde</Label>
                <Select 
                  value={formData.related_to_id} 
                  onValueChange={(v) => setFormData({ ...formData, related_to_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Velg kunde" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {formData.related_to_type === "investor" && (
              <div className="space-y-2">
                <Label htmlFor="related_to_id">Velg Investor</Label>
                <Select 
                  value={formData.related_to_id} 
                  onValueChange={(v) => setFormData({ ...formData, related_to_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Velg investor" />
                  </SelectTrigger>
                  <SelectContent>
                    {investors.map((investor) => (
                      <SelectItem key={investor.id} value={investor.id}>
                        {investor.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Avbryt</Button>
            <Button type="submit" disabled={isLoading} className="bg-slate-900 hover:bg-slate-800">
              {isLoading ? "Lagrer..." : team ? "Oppdater" : "Opprett"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}